import { makeStyles } from '@mui/styles';
import Autocomplete from 'apollo-react/components/Autocomplete';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Modal from 'apollo-react/components/Modal';
import Typography from 'apollo-react/components/Typography';
import Search from 'apollo-react-icons/Search';
import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  addHierarchyChild,
  fetchHierarchySearchResults,
} from '../../../actions/ProductHierarchyActions';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import {
  AUTOCOMPLETE_NO_OPTIONS_TEXT,
  AUTOCOMPLETE_SEARCH_DELAY,
} from '../../../form-designer/constants/runtime.constants';
import { getUITA } from '../../../utils/commonUtils';
import { isNilOrEmpty } from '../../../utils/ramdaUtils';

const useStyles = makeStyles({
  modal: {
    width: '100%',
    marginTop: '150px',
    marginBottom: 'auto',

    '& [class*="MuiDialogContent-root"]': {
      padding: '0 24px',
    },
  },
  searchMessage: {
    color: '#444444',
    paddingTop: 5,
  },
  loadingIcon: {
    marginTop: '10px',
  },
});

const SearchModal = ({ searchItem, setIsModalOpened, handleViewDetails }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [noOptionsText, setNoOptionsText] = useState(AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);

  const classes = useStyles();

  const translateString = useTranslation();
  const dispatch = useDispatch();

  const timeoutRef = useRef(null);

  const { itemId, entityName, code, label, nodeName, children } = searchItem;

  const handleModalClosed = () => {
    setIsModalOpened(false);
  };

  const handleOnChange = (_, newValue) => {
    if (!newValue) {
      setNoOptionsText(AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH);
      setSelectedProduct(null);
      setOptions([]);
      return;
    }

    if (!selectedProduct || newValue.child.itemId !== selectedProduct.itemId) {
      setSelectedProduct(newValue);
    }

    setOptions([]);
  };

  const handleInputOnChange = (_, newValue, reason) => {
    clearTimeout(timeoutRef.current);

    if (reason !== 'input') {
      return;
    }

    if (isNilOrEmpty(newValue)) {
      setOptions([]);
      setNoOptionsText(AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH);
      return;
    }

    if (!isNilOrEmpty(selectedProduct) && newValue !== selectedProduct.label) {
      setSelectedProduct(null);
    }

    timeoutRef.current = setTimeout(async () => {
      setLoading(true);

      const newOptions = await dispatch(
        fetchHierarchySearchResults({ itemId, entityName, filter: newValue })
      );

      setLoading(false);
      setOptions(newOptions);

      if (!newOptions.length && noOptionsText === AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH) {
        setNoOptionsText(AUTOCOMPLETE_NO_OPTIONS_TEXT.NO_OPTIONS);
      } else if (newOptions.length && noOptionsText === AUTOCOMPLETE_NO_OPTIONS_TEXT.NO_OPTIONS) {
        setNoOptionsText(AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH);
      }
    }, AUTOCOMPLETE_SEARCH_DELAY);
  };

  const handleViewDetailsClick = (e) => {
    e.preventDefault();

    const isChildExists = children.some((child) => child.itemId === selectedProduct.child.itemId);

    //don't add a child if it already exists in the hierarchy
    if (!isChildExists) {
      dispatch(addHierarchyChild({ itemId, newChild: selectedProduct.child }));
    }

    handleViewDetails({ childNode: selectedProduct.child });
    handleModalClosed();
  };

  const SearchTitle = () => (
    <span ui-ta={getUITA('Search Title')}>{`${translateString('Search')} - ${translateString(
      nodeName
    )} - ${code} (${label})`}</span>
  );

  return (
    <Modal
      open
      variant="default"
      className={classes.modal}
      disableBackdropClick
      onClose={handleModalClosed}
      ui-ta={getUITA('SearchModal', itemId)}
      title={<SearchTitle />}
      buttonProps={[
        {
          label: translateString('Cancel'),
          'ui-ta': getUITA('CancelButton'),
        },
        {
          disabled: isNilOrEmpty(selectedProduct),
          label: translateString('View Details'),
          'ui-ta': getUITA('ViewDetailsButton'),
          onClick: handleViewDetailsClick,
        },
      ]}
    >
      <>
        <div ui-ta={getUITA('SearchField')}>
          <Autocomplete
            fullWidth
            required
            asteriskPosition="before"
            matchFrom="any"
            label={translateString('Items')}
            showClearIndicator={false}
            clearOnBlur={false}
            className={classes.autocomplete}
            ui-ta={getUITA('SearchInput')}
            popupIcon={
              loading ? (
                <CircularProgress
                  className={classes.loadingIcon}
                  color="inherit"
                  variant="indeterminate"
                  size={24}
                />
              ) : (
                <Search />
              )
            }
            noOptionsText={translateString(noOptionsText)}
            TextFieldProps={{ placeholder: translateString('Type to search...') }}
            source={options}
            value={selectedProduct}
            onChange={handleOnChange}
            onInputChange={handleInputOnChange}
          />
        </div>
        <Typography
          ui-ta={getUITA('SearchModalMessage')}
          variant="body2"
          className={classes.searchMessage}
        >
          {`${translateString('Search by Name or Code')}`}
        </Typography>
      </>
    </Modal>
  );
};

export default SearchModal;
